// lib/stockCheck.mjs — R120 (team: "flashlight" / "Dismiss doesn't stick").
// ONE definition of the inspector's "Stocking & safety check" (the R41 card
// on Step 1 of a basic inspection) shared by the client (InspectionsPage
// renders the card and the report-card gaps from it) and the server
// (server/assetops.js tells the AI what the inspector ticked, and answers the
// AI's "not visible" findings that a tick or a same-visit count settles).
// Pure: no I/O, no clock — the caller passes the inspection, its ticks
// (form.checks) and the same-visit inventory counts.
//
// Why: the AI is told to expect a flashlight (the PAR template's "Under
// Kitchen Sink Items"), but the card had no Flashlight box, the ticks never
// reached the prompt, and nothing reconciled them — so "Fire extinguisher,
// first aid kit, and flashlight not visible" stayed open on a home where the
// inspector had ticked every one of them (Stacy, 2026-10-21).
//
// The rule that answers a finding is deliberately narrow (answeredBy):
//   · only an AI finding (never the inspector's own or an inventory count's),
//     never high / emergency severity;
//   · only an ABSENCE claim ("not visible", "not clearly visible", "missing",
//     "no … visible", "unclear" …) — a finding that names a defect (expired,
//     empty, gauge in the red, wrong spot …) is a real observation, kept;
//   · never a count shortfall (the AI saw some — observed > 0);
//   · every stocking item the note names (aliases on word boundaries, plus
//     the AI's own stock_keys tag) is confirmed by a tick or a same-visit
//     count at/above PAR, and NO named item has a same-visit count below PAR
//     (a human count of 0 means it really is missing — keep it);
//   · nothing else is named: after removing the item phrases and a fixed
//     boilerplate vocabulary no other word is left ("paper towels", "spices",
//     "smoke detector" keep the finding open).

// Midnight Pacific on the R120 deploy day: a Flashlight box only appears on
// inspections started from then on (or that already carry the key), so a
// completed 18/18 inspection never grows a "Missing: Flashlight" line — and a
// pre-deploy one reopened for editing stays 18/18 (the skeptic's correction:
// never gate on status, "Reopen & edit" sets it back to in_progress).
export const FLASHLIGHT_SINCE = '2026-10-22T07:00:00Z';

// Alias sources are matched case-insensitively on letter boundaries (a match
// may not touch another letter or digit on either side — "toilet" is never
// "oil", "peppermint" never "pepper"). Bare "soap" and "filters" are
// deliberately NOT aliases (which soap? which filter?), and 1.5× linens never
// aliases towels (towels are staged PAR counts, not the backstock).
export const STOCK_CHECK = Object.freeze([
  Object.freeze({
    key: 'supplies', label: 'Supplies',
    items: Object.freeze([
      { key: 'stk_linens', label: '1.5× linens', aliases: ['(?:bed\\s+|spare\\s+|backstock\\s+)?linens?'] },
      { key: 'stk_dishwasher_soap', label: 'Dishwasher soap', aliases: ['dish\\s*washer\\s+(?:soap|detergent|pods?|tabs?|tablets?|pacs?|packs?)'] },
      { key: 'stk_dish_soap', label: 'Dish soap', aliases: ['dish\\s*(?:washing\\s+)?(?:soap|liquid|detergent)'] },
      { key: 'stk_sponges', label: 'Sponges', aliases: ['(?:scrub(?:bing)?\\s+|kitchen\\s+)?sponges?', 'scrub(?:ber|bing)?\\s+pads?'] },
      { key: 'stk_hand_soap', label: 'Hand soap', aliases: ['hand\\s*(?:soap|wash)'] },
      { key: 'stk_body_wash', label: 'Body wash', aliases: ['body\\s*wash', 'shower\\s+gel'] },
      { key: 'stk_shampoo', label: 'Shampoo', aliases: ['shampoos?'] },
      { key: 'stk_conditioner', label: 'Conditioner', aliases: ['(?:hair\\s+)?conditioners?'], not_after: 'air' },
      { key: 'stk_trash_bags', label: 'Trash bags', aliases: ['(?:trash|garbage|bin)\\s+(?:bags?|liners?)', 'trash\\s*can\\s+liners?'] },
      { key: 'stk_cooking_oil', label: 'Cooking oil', aliases: ['(?:cooking\\s+|olive\\s+|vegetable\\s+|canola\\s+)?oils?'] },
      { key: 'stk_salt', label: 'Salt', aliases: ['salt'] },
      { key: 'stk_pepper', label: 'Pepper', aliases: ['pepper'] },
      { key: 'stk_coffee', label: 'Coffee', aliases: ['coffee(?!\\s*(?:filters?|makers?|pods?|cups?|creamers?|machines?|pots?|grinders?|mugs?|tables?)(?![a-z]))'] },
      { key: 'stk_creamer', label: 'Creamer', aliases: ['(?:coffee\\s+)?creamers?'] },
      { key: 'stk_sugar', label: 'Sugar', aliases: ['sugars?', 'sweeteners?'] },
      { key: 'stk_coffee_filters', label: 'Coffee filters', aliases: ['coffee\\s+filters?'] },
    ].map(Object.freeze)),
  }),
  Object.freeze({
    key: 'safety', label: 'Safety',
    items: Object.freeze([
      { key: 'saf_first_aid', label: 'First aid kit', aliases: ['first(?:\\s*|-)aid(?:\\s+kits?)?'] },
      { key: 'saf_fire_ext', label: 'Fire extinguisher', aliases: ['(?:fire\\s+)?extinguishers?'] },
      { key: 'saf_flashlight', label: 'Flashlight', aliases: ['flash[\\s-]*lights?', 'torch(?:es)?'], since_at: FLASHLIGHT_SINCE },
    ].map(Object.freeze)),
  }),
]);

export const ALL_ITEMS = Object.freeze(STOCK_CHECK.flatMap(g => g.items));
const BY_KEY = new Map(ALL_ITEMS.map(i => [i.key, i]));
export function itemByKey(key) { return BY_KEY.get(String(key || '')) || null; }
export function isStockKey(key) { return BY_KEY.has(String(key || '')); }
export function labelsFor(keys) { return (Array.isArray(keys) ? keys : []).map(k => (BY_KEY.get(k) || { label: k }).label); }

// No regex lookbehind anywhere (an iPhone before iOS 16.4 throws on it at
// module load, which would take the whole Inspections page down): the left
// boundary is a captured prefix, and "air conditioner" is a not_after word.
const ALIAS_RES = ALL_ITEMS.map(it => ({
  key: it.key,
  notAfter: it.not_after ? new RegExp(`(?:^|[^a-z0-9])${it.not_after}\\s+$`) : null,
  res: it.aliases.map(src => new RegExp(`(^|[^a-z0-9])(${src})(?![a-z0-9])`, 'gi')),
}));

const norm = (s) => String(s == null ? '' : s).toLowerCase().replace(/[‘’ʼ]/g, "'").replace(/[‐-―]/g, '-');

// Every stocking-item phrase in a text: { keys, spans:[{start,end,key,text}] }
// (overlapping matches keep the longer one — "coffee creamer" is the creamer).
function aliasSpans(text) {
  const t = norm(text);
  const spans = [];
  for (const { key, res, notAfter } of ALIAS_RES) {
    for (const re of res) {
      re.lastIndex = 0;
      let m;
      while ((m = re.exec(t))) {
        if (!m[2]) { re.lastIndex = m.index + 1; continue; }
        const start = m.index + m[1].length;
        if (notAfter && notAfter.test(t.slice(0, start))) continue;
        spans.push({ start, end: start + m[2].length, key, text: m[2] });
      }
    }
  }
  spans.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start));
  const out = [];
  for (const s of spans) {
    const last = out[out.length - 1];
    if (last && s.start < last.end) continue;   // overlapped by a longer / earlier phrase
    out.push(s);
  }
  return { t, spans: out };
}

// The words a "nothing else is named" check may ignore: claim wording,
// grammar, where-it-lives and the frames themselves.
const BOILERPLATE = new Set(`
a an the and or nor but yet so of in on at to into onto from for by with within without as per via
any all each every either neither both some no not none is are was were be been being am has have had
do does did could can cannot would should may might must will shall this that these those it its their
there here which who what where when than then also too only still yet just
visible visibly clearly clear readily obviously easily fully directly currently definitively explicitly
seen shown found observed located present evident documented verified confirmed stocked restocked
captured pictured photographed identified apparent spotted detected displayed provided available staged
placed set out missing unclear absent unverified unconfirmed verifiable confirmable lacking lacks lack
appear appears appeared seem seems seemed despite although though while however whether
frame frames photo photos photograph photographs image images picture pictures video videos
walkthrough footage shot shots view views camera evidence inspection inspector visit sign
kitchen bathroom bathrooms bath baths bedroom bedrooms living room rooms area areas under sink sinks
cabinet cabinets cupboard cupboards closet closets storage pantry drawer drawers shelf shelves
counter counters countertop countertops hallway hall garage laundry utility entry entryway
home house property unit location locations spot place designated usual expected standard required
requirement requirements item items supply supplies stock stocking consumables essentials par
presence visibility status confirm verify check checked anywhere during across throughout
listed list checklist safety kit kits equipment guest guests site onsite one two three
`.split(/\s+/).filter(Boolean));
const isBoilerplate = (w) => BOILERPLATE.has(w) || (w.endsWith('s') && BOILERPLATE.has(w.slice(0, -1)));
// Review R120 #2: a 2-letter word is a NAME until proven otherwise ("TV",
// "TP", "AC", "CO" kept a bundled note open only by luck before). Only this
// fixed set of tokenising debris is ignored: ordinal tails ("2nd" → "nd"),
// contraction tails ("should've" → "ve"), units and a few function words the
// boilerplate list has no 2-letter entry for. A single letter is always
// debris ("K-cups" → "k"); "A/C" and "Wi-Fi" are joined first so they stay
// whole words.
const SHORT_DEBRIS = new Set('nd st rd th re ve ll oz fl ml lb kg cm mm ft qt pk pc ct ea ok vs eg ie up if we us my me he go pm'.split(' '));
const isResidualWord = (w) => (w.length >= 3 || (w.length === 2 && !SHORT_DEBRIS.has(w))) && !isBoilerplate(w);

// keys named in a text, the matched phrases in order, and the left-over
// (non-boilerplate) words.
export function matchText(text) {
  const { t, spans } = aliasSpans(text);
  let rest = '';
  let at = 0;
  for (const s of spans) { rest += t.slice(at, s.start) + ' '; at = s.end; }
  rest += t.slice(at);
  const residual = rest.replace(/'s\b/g, '').replace(/\ba\s*\/\s*c\b/g, ' ac ').replace(/\bwi[\s-]?fi\b/g, ' wifi ')
    .replace(/[^a-z]+/g, ' ').split(' ').filter(isResidualWord);
  return { keys: [...new Set(spans.map(s => s.key))], phrases: spans.map(s => s.text), residual };
}
export function keysForText(text) { return matchText(text).keys; }

// A coverage-gap / checklist title split into its stocking phrases and the
// rest, for the report card's "extinguisher & first aid ticked stocked;
// exits not shown". rest keeps the title's own words (minus connectors).
export function splitTitle(text) {
  const raw = String(text == null ? '' : text);
  const { spans } = aliasSpans(raw);
  let rest = ''; let at = 0;
  for (const s of spans) { rest += raw.slice(at, s.start) + ' '; at = s.end; }
  rest += raw.slice(at);
  rest = rest.replace(/[,&/+;:()]+/g, ' ').replace(/\b(?:and|or|the|of)\b/gi, ' ').replace(/\s+/g, ' ').trim();
  return { phrases: spans.map(s => raw.slice(s.start, s.end)), keys: [...new Set(spans.map(s => s.key))], rest };
}

// ── absence vs defect ────────────────────────────────────────────────────
const ADVERB = '(?:clearly|visibly|readily|obviously|immediately|easily|fully|directly|currently|definitively|explicitly|actually)';
const SEEN = '(?:visible|seen|shown|found|observed|located|present|evident|documented|verified|confirmed|stocked|captured|pictured|photographed|identified|apparent|spotted|detected|displayed|staged|placed|provided|available|set\\s+out|in\\s+(?:any\\s+|the\\s+)?(?:frames?|views?|photos?|images?|shots?|walkthrough))';
const ABSENCE = [
  new RegExp(`(?:\\bnot|n't|\\bnever)\\s+(?:(?:be|been|being)\\s+)?(?:${ADVERB}\\s+)?(?:(?:be|been)\\s+)?${SEEN}(?![a-z])`),
  /\bmissing\b/,
  /\bno\s+(?:[a-z-]+\s+){0,8}(?:visible|seen|shown|found|observed|evident|present|located|in\s+(?:any\s+)?frames?)(?![a-z])/,
  /\bnone\s+(?:visible|seen|found|observed|shown|present)\b/,
  /\b(?:unclear|absent|unverified|unconfirmed|unverifiable)\b/,
  /\bnot\s+(?:verifiable|confirmable)\b/,
  /\b(?:cannot|can't|couldn't|could\s+not|unable\s+to)\s+(?:be\s+)?(?:see|seen|locate|located|find|found|verify|verified|confirm|confirmed|spot|spotted|observe|observed|identify|identified)\b/,
  /\bno\s+(?:evidence|photos?|images?|frames?|sign)\s+of\b/,
  /\black(?:s|ing)?\b/,
];
// A concrete observation about the item — it was SEEN, so a tick says nothing.
const DEFECT = /\b(?:expired|expiring|expiry|expires|out\s+of\s+date|empty|emptied|damaged|broken|discharged|used|gauge|red\s+zone|wrong\s+(?:spot|place|location|room|area)|misplaced|low|running\s+low|nearly\s+(?:out|empty)|depleted|half|leak(?:s|ing|y)?|cracked|dirty|stained|rust(?:ed|y)?|recharge|refill|overdue|unmounted|not\s+mounted|blocked|obstructed|inaccessible|tag|tagged|pin|tamper|opened|spilled|moldy|mould|mold)\b/;

export function isAbsenceClaim(note) {
  const t = norm(note);
  if (!t.trim()) return false;
  if (DEFECT.test(t)) return false;
  return ABSENCE.some(re => re.test(t));
}

// ── which items apply to an inspection (the since-rule) ──────────────────
const hasOwn = (o, k) => !!o && Object.prototype.hasOwnProperty.call(o, k);
const checksOf = (insp) => (insp && insp.form && insp.form.checks && typeof insp.form.checks === 'object') ? insp.form.checks : {};
export function applicableItems(insp) {
  const checks = checksOf(insp);
  const created = Date.parse(String((insp && insp.created_at) || ''));
  return ALL_ITEMS.filter(it => !it.since_at || hasOwn(checks, it.key) || !Number.isFinite(created) || created >= Date.parse(it.since_at));
}
export function applicableGroups(insp) {
  const keys = new Set(applicableItems(insp).map(i => i.key));
  return STOCK_CHECK.map(g => ({ key: g.key, label: g.label, items: g.items.filter(i => keys.has(i.key)) }));
}
// The report card's outcome — null when the check was never started (an
// untouched check reads "not done", not "19 missing").
export function stockGaps(insp) {
  const checks = checksOf(insp);
  const all = applicableItems(insp);
  const done = all.filter(i => !!checks[i.key]).length;
  if (!done) return null;
  return { missing: all.filter(i => !checks[i.key]).map(i => i.label), done, total: all.length };
}

// ── same-visit counts → per key ──────────────────────────────────────────
// counts: [{ item, qty, expected }] — the PAR inventory counts saved on the
// SAME listing on the visit's business day (the caller filters by day).
const needOf = (c) => Math.max(1, Number(c && c.expected) > 0 ? Number(c.expected) : 1);
function countsByKey(counts) {
  const m = new Map();
  for (const c of (Array.isArray(counts) ? counts : [])) {
    if (!c || c.item == null) continue;
    const qty = Number(c.qty);
    if (!Number.isFinite(qty)) continue;
    for (const k of keysForText(c.item)) {
      if (!m.has(k)) m.set(k, []);
      m.get(k).push({ item: String(c.item), qty, expected: c.expected == null ? null : Number(c.expected) });
    }
  }
  return m;
}
// 'ticked' | 'counted' | 'conflict' (a count below PAR) | null (unconfirmed)
function keyState(k, checks, byKey) {
  const cs = byKey.get(k) || [];
  if (cs.some(c => c.qty < needOf(c))) return 'conflict';
  if (checks && checks[k] === true) return 'ticked';
  if (cs.length) return 'counted';
  return null;
}

// Is this finding settled by the inspector's stocking check?
//   issue: { source, severity, note, observed, stock_keys? }
//   checks: form.checks · counts: same-visit counts (see above)
//   items: the applicable items (applicableItems) — a stock_keys tag outside
//          the known keys is ignored
//   → { answered, keys, counts? } (counts = { item: qty } that confirmed it)
export function answeredBy(issue, checks, counts, items) {
  const no = { answered: false, keys: [] };
  if (!issue || typeof issue !== 'object') return no;
  const src = String(issue.source || '');
  if (src === 'inspector' || src === 'inventory_count') return no;
  const sev = String(issue.severity || '').toLowerCase();
  if (sev === 'high' || sev === 'emergency') return no;
  const note = String(issue.note || '');
  if (!isAbsenceClaim(note)) return no;
  if (issue.observed != null && Number(issue.observed) > 0) return no;   // a count shortfall — the AI saw some
  const known = new Set((Array.isArray(items) && items.length ? items : ALL_ITEMS).map(i => i.key));
  const m = matchText(note);
  const tagged = (Array.isArray(issue.stock_keys) ? issue.stock_keys : []).map(String).filter(k => known.has(k));
  // alias matches are NOT filtered by applicability: a note naming an item
  // this inspection has no box for can only be settled by a count
  const keys = [...new Set([...tagged, ...m.keys])];
  if (!keys.length || m.residual.length) return { ...no, keys };
  const byKey = countsByKey(counts);
  const used = {};
  for (const k of keys) {
    const st = keyState(k, checks, byKey);
    if (st !== 'ticked' && st !== 'counted') return { ...no, keys };
    for (const c of (byKey.get(k) || [])) used[c.item] = c.qty;
  }
  return { answered: true, keys, ...(Object.keys(used).length ? { counts: used } : {}) };
}
export function partitionAnswered(issues, checks, counts, items) {
  const answered = [], open = [];
  for (const iss of (Array.isArray(issues) ? issues : [])) {
    const a = answeredBy(iss, checks, counts, items);
    if (a.answered) answered.push({ issue: iss, keys: a.keys, ...(a.counts ? { counts: a.counts } : {}) });
    else open.push(iss);
  }
  return { answered, open };
}

// The auto-dismiss reason (contract C1): "Inspector ticked stocked: <labels>"
// — a key confirmed only by a same-visit count says so.
export function answeredReason(keys, checks, counts) {
  const byKey = countsByKey(counts);
  const ticked = (keys || []).filter(k => checks && checks[k] === true);
  const counted = (keys || []).filter(k => !(checks && checks[k] === true));
  const cnt = counted.flatMap(k => (byKey.get(k) || []).map(c => `${c.item} ${c.qty}`));
  const parts = [];
  if (ticked.length) parts.push(`Inspector ticked stocked: ${labelsFor(ticked).join(', ')}`);
  if (cnt.length) parts.push(`${ticked.length ? 'counted on site' : 'Counted on site'}: ${[...new Set(cnt)].join(', ')}`);
  return parts.join(' · ');
}

// Coverage gaps (checklist titles never shown in a frame) whose stocking
// parts are all confirmed → { [title]: keys } (an annotation — the gap stays).
export function coverageAnswered(titles, checks, counts) {
  const byKey = countsByKey(counts);
  const out = {};
  for (const title of (Array.isArray(titles) ? titles : [])) {
    const keys = keysForText(title);
    if (!keys.length) continue;
    if (keys.every(k => { const st = keyState(k, checks, byKey); return st === 'ticked' || st === 'counted'; })) out[String(title)] = keys;
  }
  return out;
}

// ── a reworded copy of a finding a human already settled ─────────────────
// Re-analyze used to bring back "First aid kit not clearly evident" after the
// inspector dismissed "First aid kit not visible" (and as maintenance, not
// safety). Review R120 #1: the match is the plan's narrow one and nothing
// more. a = the FRESH finding, b = the settled one; ALL must hold:
//   · both in the stocking family (supply / safety / maintenance; never
//     damage or cleanliness);
//   · a is the AI's, not high / emergency severity, not a count shortfall;
//   · both are absence claims ("not visible" …), never a defect;
//   · same item_key (null counts as same);
//   · every stocking item a names (aliases + its validated stock_keys) is one
//     b named, and a names nothing else (the answeredBy residual rule).
// A shared ordinary word ("wall", "toilet", "smoke detector") never counts:
// "Large hole in living room wall" is not "Scuff marks on living room wall".
// The caller never drops a match: it keeps it on the record, dismissed as a
// duplicate that points at b (auditable, restorable).
const STOCK_FAMILY = new Set(['supply', 'safety', 'maintenance']);
const noteKeys = (iss, m) => [...new Set([...(Array.isArray(iss.stock_keys) ? iss.stock_keys.map(String).filter(k => BY_KEY.has(k)) : []), ...m.keys])];
export function sameConcern(a, b) {
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  if (!STOCK_FAMILY.has(String(a.type || '')) || !STOCK_FAMILY.has(String(b.type || ''))) return false;
  const src = String(a.source || '');
  if (src === 'inspector' || src === 'inventory_count') return false;
  const sev = String(a.severity || '').toLowerCase();
  if (sev === 'high' || sev === 'emergency') return false;
  if (a.observed != null && Number(a.observed) > 0) return false;
  if ((a.item_key == null ? null : String(a.item_key)) !== (b.item_key == null ? null : String(b.item_key))) return false;
  // "not visible" and "expired" about the same kit are different concerns —
  // a dismissal as present never swallows a newly seen defect
  if (!isAbsenceClaim(a.note) || !isAbsenceClaim(b.note)) return false;
  const ma = matchText(a.note);
  if (ma.residual.length) return false;
  const ka = noteKeys(a, ma);
  const kb = new Set(noteKeys(b, matchText(b.note)));
  return ka.length > 0 && ka.every(k => kb.has(k));
}
// The stocking keys a finding names (aliases + validated stock_keys).
export function findingKeys(iss) {
  return iss && typeof iss === 'object' ? noteKeys(iss, matchText(iss.note)) : [];
}

// A finding a human closed as already-checked: a native dismissal with
// reason present / not_issue, or a legacy one (no via) — fed back to the AI
// as "already reviewed on site", and the corpus of the re-analyze dedupe.
export function humanReviewed(iss) {
  const a = iss && iss.action;
  if (!a || a.kind !== 'dismissed') return false;
  if (!a.via || a.via === 'legacy') return true;
  if (a.via === 'auto') return false;
  return a.reason_code === 'present' || a.reason_code === 'not_issue';
}

// ── the prompt clause ────────────────────────────────────────────────────
// What the inspector confirmed on site, for the grading prompt. Empty when
// the check was never touched, nothing was counted and nothing reviewed.
export function promptClause(insp, counts) {
  const items = applicableItems(insp);
  const checks = checksOf(insp);
  const lines = [];
  if (items.some(i => hasOwn(checks, i.key))) {
    const yes = items.filter(i => checks[i.key] === true);
    const notTicked = items.filter(i => checks[i.key] !== true);
    lines.push("INSPECTOR'S STOCKING & SAFETY CHECK (ticked on site, human-confirmed).");
    if (yes.length) lines.push(`CONFIRMED STOCKED: ${yes.map(i => `${i.label} (${i.key})`).join(', ')}`);
    if (notTicked.length) lines.push(`NOT TICKED: ${notTicked.map(i => `${i.label} (${i.key})`).join(', ')} — An unticked box is NOT evidence that an item is missing.`);
  }
  const cs = (Array.isArray(counts) ? counts : []).filter(c => c && c.item != null && Number.isFinite(Number(c.qty))).slice(0, 30);
  if (cs.length) lines.push(`Counted on site today: ${cs.map(c => `${c.item} ${Number(c.qty)}${c.expected != null ? ` (expect ${c.expected})` : ''}`).join(', ')}`);
  const reviewed = (Array.isArray(insp && insp.issues) ? insp.issues : []).filter(humanReviewed)
    .map(x => String(x.note || '').trim()).filter(Boolean);
  if (reviewed.length) {
    // review R120 #1: a list of what a person already checked, never an order
    // to bury a new problem (the grader's rule line says what to do with it)
    lines.push('ALREADY REVIEWED ON SITE (a person checked these on this visit and dismissed them):');
    for (const n of [...new Set(reviewed)].slice(0, 15)) lines.push(`  - ${n.slice(0, 160)}`);
  }
  return lines.length ? `\n${lines.join('\n')}` : '';
}
